// ============================================================
// TERROR — THE TRADE POST (UI)
// ------------------------------------------------------------
// Two tabs over one engine (trading.js):
//
//   TRADE — pick a friend → propose; or arrive with `initialTradeId`
//           from an invite / ?trade= param. Both offers side by side,
//           mine editable until locked, LOCK / CONFIRM / CANCEL driven
//           off the shared node's state, a status banner, and the
//           pending list (halves that debited and still owe the player
//           a Collect, or a Reclaim after expiry).
//   SELL  — the scrap lane, surfaced. Dupes and foils → ◕ at
//           SCRAP_COIN prices; the last plain copy is protected by the
//           engine's floor of one; a sale that undercuts a saved set
//           WARNS and lets the player decide.
//
// THE UI NEVER MOVES A CARD. Every write goes through trading.js, and
// the settlement effect below only ever asks the engine to run the
// idempotent step the shared node says is due. Refreshing mid-trade is
// safe by construction — the sealed copy remembers where we were.
//
// Props:
//   profile        the live profile object (collection + chocolateCoins are read off it)
//   uid            the signed-in Auth uid (the engine re-checks it against auth.currentUser)
//   playerName     display name (clipped before it is ever stored)
//   onBack         leave the post
//   initialTradeId open this trade on mount (from an invite / ?trade=)
//   friends        [{uid, name, online}] from the social bridge
//   onInviteFriend (friendUid, tradeId) → caller sends F.invite(...) with tradeInviteOpts(id)
// ============================================================

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { GM, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS, OBSIDIAN_BG, TEXT_SET } from "./theme";
import { useStageRect } from "./RoomBackdrop"; // C5 — the one canonical room rectangle
import { useModalKeys } from "./KeyboardNav";
import { OrnatePanel, OrnateDivider, TerrorButton, TerrorHeader, ChipBadge } from "./TerrorUI";
import {
  TRADE_COIN_CAP, DRAFT_TTL_MS,
  emptyOffer, normalizeOffer, offerIsEmpty, offerToItems, tradeableOf, validateOffer,
  deckWarningsFor, sellValueOf, sellCopies,
  proposeTrade, updateOffer, lockOffer, unlockOffer, confirmTrade, cancelTrade,
  settleDebit, settleCredit, reclaimTrade,
  sideOf, otherSideOf, isExpired,
  usePendingTrades, useTrade, useSealed,
  cardOf, cardName, tierLabel,
} from "./trading";
import { serverNow } from "./ieSocial";

const COIN = "◕";

// ── small style atoms — the house look, no new palette ───────
const S = {
  tab: (on) => ({
    padding: "9px 18px", cursor: "pointer", fontFamily: GM.font, fontSize: 11, fontWeight: 900,
    letterSpacing: 3, color: on ? GM.inkGoldBright : GM.vellumFaint,
    background: on ? OBSIDIAN_SLAB_LIT : "transparent",
    border: `1px solid ${on ? GM.inkGold : GM.pageBorder}`, borderRadius: 6,
    textShadow: TEXT_SET,
  }),
  box: { background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`, borderRadius: 8, padding: 10 },
  small: { fontSize: 10, color: GM.vellumFaint, letterSpacing: 1.5, fontFamily: GM.font },
  body: { fontSize: 12.5, color: GM.vellumDim, fontFamily: GM.fontBody },
  err: { fontSize: 11.5, color: "#e88585", marginTop: 6 },
  warn: { fontSize: 11.5, color: GM.inkGoldBright, marginTop: 6 },
  input: {
    width: 120, padding: "6px 8px", background: GM.page, color: GM.vellum,
    border: `1px solid ${GM.pageBorder}`, borderRadius: 6, fontFamily: GM.fontUI, fontSize: 13,
  },
  mini: (on, color = GM.inkGold) => ({
    padding: "3px 8px", borderRadius: 5, cursor: "pointer", fontFamily: GM.fontUI, fontSize: 11, fontWeight: 700,
    color: on ? "#000" : color, background: on ? color : "transparent",
    border: `1px solid ${color}`, lineHeight: 1.2,
  }),
};

const TIER_COLORS = {
  holo_mythic: "#ff66cc", holo_gold: "#e8b84b", holo_silver: "#c8ccd8", holo_solrend: "#ff2266",
  holo_aeternus: "#f0f0ff", holo_depths: "#00aaff", holo_khao: "#9333ea", holo_jynxi: "#c084fc",
  holo_void: "#9333ea", holo_shadow: "#9966ff",
};
const tierColor = (t) => TIER_COLORS[t] || GM.amethystBright;

const fmt = (n) => (Number(n) || 0).toLocaleString();
const mmss = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  if (h > 0) return `${h}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// A once-a-second tick so countdowns and expiry states re-render.
function useTick(ms = 1000) {
  const [, setT] = useState(0);
  useEffect(() => { const i = setInterval(() => setT((x) => x + 1), ms); return () => clearInterval(i); }, [ms]);
}

// ════════════════════════════════════════════════════════════
// CARD PICKER — a flex-wrap grid of owned cards. Each tile shows the
// name, the plain count and one chip per foil tier; tapping a count
// or chip adds one to the selection (up to the max), the small "−"
// takes one back. `options` = {cardId:{plain, tiers:{tier:max}}}.
// ════════════════════════════════════════════════════════════
function CardPicker({ options, value, onChange, emptyText = "Nothing to offer." }) {
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const ids = Object.keys(options || {}).filter((id) => cardOf(id));
    const needle = q.trim().toLowerCase();
    return ids
      .filter((id) => !needle || cardName(id).toLowerCase().includes(needle))
      .sort((a, b) => cardName(a).localeCompare(cardName(b)));
  }, [options, q]);

  const sel = value || {};
  const setPlain = (id, n) => {
    const next = { ...sel };
    const cur = next[id] || { plain: 0, tiers: {} };
    const max = options[id]?.plain || 0;
    const plain = Math.max(0, Math.min(max, n));
    const tiers = { ...(cur.tiers || {}) };
    if (plain === 0 && !Object.keys(tiers).length) delete next[id]; else next[id] = { plain, tiers };
    onChange(next);
  };
  const setTier = (id, t, n) => {
    const next = { ...sel };
    const cur = next[id] || { plain: 0, tiers: {} };
    const max = options[id]?.tiers?.[t] || 0;
    const tiers = { ...(cur.tiers || {}) };
    const k = Math.max(0, Math.min(max, n));
    if (k > 0) tiers[t] = k; else delete tiers[t];
    if ((cur.plain || 0) === 0 && !Object.keys(tiers).length) delete next[id]; else next[id] = { plain: cur.plain || 0, tiers };
    onChange(next);
  };

  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your cards"
        style={{ ...S.input, width: "100%", boxSizing: "border-box", marginBottom: 8 }} />
      {!rows.length && <div style={{ ...S.body, fontStyle: "italic", padding: "6px 0" }}>{emptyText}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 320, overflowY: "auto", paddingRight: 2 }}>
        {rows.map((id) => {
          const opt = options[id];
          const card = cardOf(id);
          const picked = sel[id] || { plain: 0, tiers: {} };
          const any = (picked.plain || 0) > 0 || Object.keys(picked.tiers || {}).length > 0;
          return (
            <div key={id} style={{
              flex: "1 1 150px", minWidth: 150, maxWidth: 240,
              background: any ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
              border: `1px solid ${any ? GM.inkGold : GM.pageBorder}`, borderRadius: 8, padding: "8px 10px",
              boxShadow: any ? `0 0 10px ${GM.inkGold}33` : "none",
            }}>
              <div style={{ fontSize: 11.5, color: GM.vellum, fontFamily: GM.font, fontWeight: 700, lineHeight: 1.3, marginBottom: 2 }}>{card.name}</div>
              <div style={{ ...S.small, marginBottom: 6 }}>{card.type}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
                {opt.plain > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <button onClick={() => setPlain(id, (picked.plain || 0) + 1)} style={S.mini((picked.plain || 0) > 0, GM.inkGoldBright)}
                      title="Plain copy">
                      ◇ {picked.plain || 0}/{opt.plain}
                    </button>
                    {(picked.plain || 0) > 0 && <button onClick={() => setPlain(id, picked.plain - 1)} style={S.mini(false, GM.vellumFaint)}>−</button>}
                  </span>
                )}
                {Object.entries(opt.tiers || {}).map(([t, max]) => (
                  <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <button onClick={() => setTier(id, t, (picked.tiers?.[t] || 0) + 1)} style={S.mini((picked.tiers?.[t] || 0) > 0, tierColor(t))}
                      title={`${tierLabel(t)} foil`}>
                      ◈ {tierLabel(t)} {picked.tiers?.[t] || 0}/{max}
                    </button>
                    {(picked.tiers?.[t] || 0) > 0 && <button onClick={() => setTier(id, t, picked.tiers[t] - 1)} style={S.mini(false, GM.vellumFaint)}>−</button>}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── OFFER VIEW — read-only summary of an offer ───────────────
function OfferView({ offer, title, accent = GM.inkGold, emptyText = "Nothing yet." }) {
  const o = normalizeOffer(offer);
  const items = offerToItems(o);
  return (
    <div style={{ ...S.box, borderColor: `${accent}66` }}>
      <div style={{ ...S.small, color: accent, marginBottom: 6 }}>{title}</div>
      {items.length === 0 && o.coins === 0 && <div style={{ ...S.body, fontStyle: "italic" }}>{emptyText}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {o.coins > 0 && <ChipBadge icon={COIN} color={GM.inkGoldBright}>{fmt(o.coins)}</ChipBadge>}
        {items.map((it, i) => (
          <ChipBadge key={i} icon={it.tier ? "◈" : "◇"} color={it.tier ? tierColor(it.tier) : GM.silver}>
            {cardName(it.cardId)}{it.tier ? ` · ${tierLabel(it.tier)}` : ""} ×{it.copies}
          </ChipBadge>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// THE TRADE BOARD — one open trade
// ════════════════════════════════════════════════════════════
function TradeBoard({ tradeId, uid, profile, onClose }) {
  useTick(1000);
  const trade  = useTrade(tradeId);
  const sealed = useSealed(uid, tradeId);
  const side   = sideOf(trade, uid);
  const other  = otherSideOf(side);
  const mine   = side ? trade[side] : null;
  const theirs = other ? trade[other] : null;

  const [draft, setDraft] = useState(emptyOffer());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState("");
  const [err, setErr]     = useState("");

  const collection = profile?.collection || {};
  const coins = profile?.chocolateCoins || 0;
  const tradeable = useMemo(() => tradeableOf(collection), [collection]);

  // Mirror the shared node into the local draft until I start editing.
  useEffect(() => {
    if (!mine || dirty) return;
    setDraft(normalizeOffer(mine.offer));
  }, [mine?.offer, mine, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = trade?.status || "…";
  const expired = isExpired(trade) && !["settled", "cancelled", "expired"].includes(status);
  const editable = !!mine && ["draft", "locked"].includes(status) && !mine.locked && !expired;
  const canLock = !!mine && ["draft", "locked"].includes(status) && !mine.locked && !dirty && !expired
    && !(offerIsEmpty(mine.offer) && offerIsEmpty(theirs?.offer));
  const canConfirm = status === "locked" && !!mine?.locked && !!theirs?.locked && !mine?.confirmed && !expired;
  const canCancel = !!mine && !["settled", "cancelled", "expired"].includes(status) && !trade?.a?.debited && !trade?.b?.debited;
  const canUnlock = !!mine?.locked && ["draft", "locked"].includes(status) && !mine?.confirmed;

  const validation = useMemo(() => validateOffer(collection, draft, { coins }), [collection, draft, coins]);

  // ── THE SETTLEMENT EFFECT ───────────────────────────────────
  // The only automation in the file. When the shared node says both
  // confirmed and my seal has not debited → debit. When my seal has
  // debited, not credited, and the other side's debit is on the shared
  // node → credit. Both engine calls are idempotent on the seal, and
  // the ref keeps a re-render from launching the same step twice.
  const stepRef = useRef("");
  useEffect(() => {
    if (!trade || !side || !uid) return;
    const run = async (key, fn) => {
      if (stepRef.current === key) return;
      stepRef.current = key;
      try { const r = await fn(); if (r && !r.ok && r.error && !r.waiting && !r.busy) setErr(r.error); }
      catch (e) { setErr(e?.message || "Settlement failed."); }
      finally { if (stepRef.current === key) stepRef.current = ""; }
    };
    if (["confirmed", "debited"].includes(trade.status) && mine?.confirmed && sealed && !sealed.debitedAt
        && (sealed.rev || 0) === (trade.rev || 0)) {
      run(`debit:${tradeId}`, () => settleDebit(uid, tradeId));
    } else if (sealed?.debitedAt && !sealed.creditedAt && !sealed.reclaimedAt && theirs?.debited) {
      run(`credit:${tradeId}`, () => settleCredit(uid, tradeId));
    }
  }, [trade, sealed, side, uid, tradeId, mine?.confirmed, theirs?.debited]);

  const act = async (fn, okMsg = "") => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await fn();
      if (r?.ok) { if (okMsg) setMsg(okMsg); }
      else setErr(r?.error || "That did not go through.");
    } catch (e) { setErr(e?.message || "That did not go through."); }
    setBusy(false);
  };

  const saveOffer = () => act(async () => {
    const r = await updateOffer(tradeId, uid, draft, { collection, coins });
    if (r.ok) setDirty(false);
    return r;
  }, "Offer updated. Locks cleared on both sides.");

  // ── banner text ─────────────────────────────────────────────
  const banner = (() => {
    if (!trade) return { text: "Opening the trade…", color: GM.vellumFaint };
    if (!side)  return { text: "This trade is between two other players.", color: "#e88585" };
    const left = typeof trade.expiresAt === "number" ? trade.expiresAt - serverNow() : 0;
    switch (status) {
      case "draft":     return expired ? { text: "Expired — unlock or edit to renew.", color: "#e88585" }
        : { text: `Drafting · both sides edit freely · ${mmss(left)} left`, color: GM.vellumDim };
      case "locked":    return expired ? { text: "Expired before confirmation.", color: "#e88585" }
        : { text: mine?.locked && theirs?.locked ? `Both locked · confirm to seal your half · ${mmss(left)} left`
          : mine?.locked ? "You are locked · waiting on the other side" : "They are locked · lock when you agree", color: GM.inkGoldBright };
      case "confirmed": return { text: sealed?.debitedAt ? "Your half has left · waiting for theirs" : "Both confirmed · moving your half…", color: GM.amethystBright };
      case "debited":   return { text: sealed?.creditedAt ? "Collected · waiting on the other side's credit" : "Both halves moved · collecting…", color: GM.amethystBright };
      case "settled":   return { text: "Settled. The cards have found their new homes.", color: "#4ade80" };
      case "cancelled": return { text: trade.cancelledBy === uid ? "You cancelled this trade." : "The other side cancelled this trade.", color: "#e88585" };
      case "expired":   return { text: "This trade expired.", color: "#e88585" };
      default:          return { text: status, color: GM.vellumFaint };
    }
  })();

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ fontSize: 13, color: GM.vellum, fontFamily: GM.font, fontWeight: 700, flex: 1, minWidth: 160 }}>
          {mine?.name || "You"} <span style={{ color: GM.vellumFaint }}>◆</span> {theirs?.name || "…"}
        </div>
        <ChipBadge icon="◈" color={GM.silver}>rev {trade?.rev || 0}</ChipBadge>
        <button onClick={onClose} style={S.tab(false)}>CLOSE</button>
      </div>
      <div style={{ ...S.box, borderColor: `${banner.color}66`, color: banner.color, fontSize: 12, fontFamily: GM.font, letterSpacing: 1, marginBottom: 10 }}>
        {banner.text}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {/* MY HALF */}
        <div style={{ flex: "1 1 300px", minWidth: 260 }}>
          <TerrorHeader size={10}>Your offer</TerrorHeader>
          <div style={{ height: 8 }} />
          {editable ? (
            <div style={S.box}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                <span style={S.small}>{COIN} COINS</span>
                <input type="number" min={0} max={Math.min(TRADE_COIN_CAP, coins)} value={draft.coins}
                  onChange={(e) => { setDirty(true); setDraft({ ...draft, coins: Math.max(0, Math.floor(Number(e.target.value) || 0)) }); }}
                  style={S.input} />
                <span style={S.small}>of {fmt(coins)} · cap {fmt(TRADE_COIN_CAP)}</span>
              </div>
              <CardPicker options={tradeable} value={draft.cards}
                onChange={(cards) => { setDirty(true); setDraft({ ...draft, cards }); }}
                emptyText="No tradeable cards. Vault singles never leave the account." />
              {!validation.ok && validation.errors.map((e, i) => <div key={i} style={S.err}>{e}</div>)}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <TerrorButton onClick={saveOffer} disabled={busy || !dirty || !validation.ok} style={{ padding: "9px 16px" }}>SAVE OFFER</TerrorButton>
                {dirty && <TerrorButton variant="ghost" onClick={() => { setDirty(false); setDraft(normalizeOffer(mine?.offer)); }} style={{ padding: "9px 16px" }}>REVERT</TerrorButton>}
              </div>
            </div>
          ) : (
            <OfferView offer={mine?.offer} title={mine?.locked ? "LOCKED" : "YOUR HALF"} accent={mine?.confirmed ? GM.amethystBright : GM.inkGold} />
          )}
        </div>

        {/* THEIR HALF */}
        <div style={{ flex: "1 1 300px", minWidth: 260 }}>
          <TerrorHeader size={10} color={GM.silver}>{theirs?.name ? `${theirs.name}'s offer` : "Their offer"}</TerrorHeader>
          <div style={{ height: 8 }} />
          <OfferView offer={theirs?.offer} accent={theirs?.confirmed ? GM.amethystBright : theirs?.locked ? GM.inkGoldBright : GM.silver}
            title={theirs?.confirmed ? "CONFIRMED" : theirs?.locked ? "LOCKED" : "DRAFTING"} emptyText="They have not put anything down." />
          {sealed && (
            <div style={{ ...S.small, marginTop: 8, lineHeight: 1.8 }}>
              Sealed at rev {sealed.rev} · {sealed.debitedAt ? "your half moved" : "not yet moved"}
              {sealed.creditedAt ? " · collected" : ""}{sealed.reclaimedAt ? " · reclaimed" : ""}
            </div>
          )}
        </div>
      </div>

      {/* ACTIONS */}
      <OrnateDivider />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {canUnlock
          ? <TerrorButton variant="teal" disabled={busy} onClick={() => act(() => unlockOffer(tradeId, uid), "Unlocked. Both locks cleared.")}>UNLOCK</TerrorButton>
          : <TerrorButton disabled={busy || !canLock} onClick={() => act(() => lockOffer(tradeId, uid), "Locked.")}>LOCK</TerrorButton>}
        <TerrorButton disabled={busy || !canConfirm}
          onClick={() => act(() => confirmTrade(tradeId, uid), "Confirmed. Your half is sealed.")}>CONFIRM</TerrorButton>
        <TerrorButton variant="crimson" disabled={busy || !canCancel} onClick={() => act(() => cancelTrade(tradeId, uid), "Cancelled.")}>CANCEL</TerrorButton>
      </div>
      {msg && <div style={{ ...S.warn, textAlign: "center" }}>{msg}</div>}
      {err && <div style={{ ...S.err, textAlign: "center" }}>{err}</div>}
      <div style={{ ...S.small, textAlign: "center", marginTop: 10, lineHeight: 1.7 }}>
        Lock when you agree with both halves. Confirm seals your half; once both confirm, each side moves its own cards and collects the other's.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PENDING HALVES — Collect / Reclaim
// ════════════════════════════════════════════════════════════
function PendingList({ uid, onOpen }) {
  useTick(1000);
  const rows = usePendingTrades(uid);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  if (!rows.length) return null;
  const go = async (id, fn) => {
    setBusy(id); setErr("");
    try { const r = await fn(); if (!r?.ok) setErr(r?.error || "Not yet."); }
    catch (e) { setErr(e?.message || "Not yet."); }
    setBusy(null);
  };
  return (
    <div style={{ marginTop: 14 }}>
      <TerrorHeader size={10} color={GM.amethystBright}>Trades still open</TerrorHeader>
      <div style={{ height: 8 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r) => {
          const past = serverNow() > Number(r.expiresAt || 0);
          return (
            <div key={r.id} style={{ ...S.box, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 12, color: GM.vellum, fontFamily: GM.font }}>with {r.counterpartyName || "a player"}</div>
                <div style={S.small}>{r.stage === "confirmed" ? "confirmed — your half not yet settled" : past ? "expired" : `reclaim in ${mmss(Number(r.expiresAt) - serverNow())}`}</div>
              </div>
              {r.stage === "confirmed" ? (
                // Confirmed, never debited: the post was closed before the
                // other side confirmed. OPEN puts the trade back on the
                // table; its settlement effect runs the debit from there.
                <TerrorButton disabled={busy === r.id} onClick={() => onOpen && onOpen(r.id)} style={{ padding: "8px 14px" }}>OPEN</TerrorButton>
              ) : (
                <>
                  <TerrorButton disabled={busy === r.id} onClick={() => go(r.id, () => settleCredit(uid, r.id))} style={{ padding: "8px 14px" }}>COLLECT</TerrorButton>
                  <TerrorButton variant="crimson" disabled={busy === r.id || !past} onClick={() => go(r.id, () => reclaimTrade(uid, r.id))} style={{ padding: "8px 14px" }}>RECLAIM</TerrorButton>
                </>
              )}
            </div>
          );
        })}
      </div>
      {err && <div style={S.err}>{err}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TRADE TAB
// ════════════════════════════════════════════════════════════
function TradeTab({ uid, playerName, profile, initialTradeId, friends, onInviteFriend }) {
  const [tradeId, setTradeId] = useState(initialTradeId || null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { if (initialTradeId) setTradeId(initialTradeId); }, [initialTradeId]);

  const roster = useMemo(() => {
    const rows = (friends || []).filter((f) => f && f.uid && f.uid !== uid);
    return rows.sort((a, b) => (b.online === a.online ? String(a.name).localeCompare(String(b.name)) : (b.online ? 1 : -1)));
  }, [friends, uid]);

  const propose = async (f) => {
    setBusy(f.uid); setErr("");
    try {
      const id = await proposeTrade(uid, playerName, f.uid, f.name);
      if (!id) { setErr("Could not open a trade."); }
      else {
        setTradeId(id);
        try { if (onInviteFriend) await onInviteFriend(f.uid, id); } catch (_) { /* the post is open either way */ }
      }
    } catch (e) { setErr(e?.message || "Could not open a trade."); }
    setBusy(null);
  };

  if (tradeId) return <TradeBoard tradeId={tradeId} uid={uid} profile={profile} onClose={() => setTradeId(null)} />;

  return (
    <div>
      <TerrorHeader size={10}>Trade with a friend</TerrorHeader>
      <div style={{ height: 8 }} />
      {!roster.length && (
        <div style={{ ...S.body, fontStyle: "italic", textAlign: "center", padding: "8px 0" }}>
          No friends on the roster yet. Trade Marks from the friends panel and they will appear here.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto" }}>
        {roster.map((f) => (
          <div key={f.uid} style={{ ...S.box, display: "flex", alignItems: "center", gap: 9, background: f.online ? "rgba(124,58,237,0.10)" : OBSIDIAN_RECESS }}>
            <span style={{ width: 7, height: 7, borderRadius: 7, flex: "0 0 auto", background: f.online ? "#4ade80" : GM.vellumFaint, boxShadow: f.online ? "0 0 7px #4ade80" : "none" }} />
            <span style={{ flex: 1, minWidth: 0, color: GM.vellumDim, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
            <button onClick={() => propose(f)} disabled={busy === f.uid} style={S.tab(false)}>{busy === f.uid ? "…" : "PROPOSE"}</button>
          </div>
        ))}
      </div>
      {err && <div style={S.err}>{err}</div>}
      <div style={{ ...S.small, marginTop: 10, lineHeight: 1.7 }}>
        A proposal sends a trade invite through the friends panel. Drafts expire after {Math.round(DRAFT_TTL_MS / 60000)} minutes of silence.
      </div>
      <PendingList uid={uid} onOpen={(id) => setTradeId(id)} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// SELL TAB — the scrap lane
// ════════════════════════════════════════════════════════════
function SellTab({ uid, profile }) {
  const collection = profile?.collection || {};
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // What may be sold: plain copies above the floor of one, and every
  // foil tier (a foil sale melts the finish; ownership survives).
  const options = useMemo(() => {
    const out = {};
    for (const [id, rec] of Object.entries(collection)) {
      if (!rec || !cardOf(id)) continue;
      const plain = Math.max(0, (Number(rec.count) || 0) - 1);
      const tiers = {};
      for (const [t, n] of Object.entries(rec.holoTiers || {})) if ((Number(n) || 0) > 0) tiers[t] = Number(n);
      if (plain > 0 || Object.keys(tiers).length) out[id] = { plain, tiers };
    }
    return out;
  }, [collection]);

  const items = useMemo(() => offerToItems({ cards: sel, coins: 0 }), [sel]);
  const payout = sellValueOf(items);
  const warnings = useMemo(() => deckWarningsFor(profile, items), [profile, items]);

  const sell = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await sellCopies(uid, items);
      if (r?.success) { setMsg(`Scrapped for ${fmt(r.paid)} ${COIN}.`); setSel({}); }
      else setErr(r?.reason === "nothing-to-scrap" ? "Nothing there to scrap." : "The sale did not go through.");
    } catch (e) { setErr(e?.message || "The sale did not go through."); }
    setBusy(false);
  };

  return (
    <div>
      <TerrorHeader size={10}>Scrap dupes for coins</TerrorHeader>
      <div style={{ height: 8 }} />
      <CardPicker options={options} value={sel} onChange={setSel} emptyText="No dupes or foils to scrap. The last copy of every card stays with you." />
      {warnings.map((w) => (
        <div key={w.cardId} style={S.warn}>
          {cardName(w.cardId)}: a saved set uses {w.used}, you would keep {w.after}. The set will show it as missing.
        </div>
      ))}
      <OrnateDivider />
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
        <ChipBadge icon={COIN} color={GM.inkGoldBright}>{fmt(payout)}</ChipBadge>
        <TerrorButton disabled={busy || !items.length} onClick={sell}>SCRAP FOR {fmt(payout)} {COIN}</TerrorButton>
      </div>
      {msg && <div style={{ ...S.warn, textAlign: "center" }}>{msg}</div>}
      {err && <div style={{ ...S.err, textAlign: "center" }}>{err}</div>}
      <div style={{ ...S.small, textAlign: "center", marginTop: 10, lineHeight: 1.7 }}>
        Plain dupes and foils only. Your last copy of a card can never be scrapped.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// THE POST
// ════════════════════════════════════════════════════════════
export default function TradePost({
  profile, uid, playerName, onBack, initialTradeId = null, friends = [], onInviteFriend,
}) {
  const [tab, setTab] = useState("trade");
  useEffect(() => { if (initialTradeId) setTab("trade"); }, [initialTradeId]);
  const coins = profile?.chocolateCoins || 0;
  const back = useCallback(() => { if (onBack) onBack(); }, [onBack]);

  // C5 — THE ONE CANONICAL ROOM RECTANGLE. Same shape as the Codex: a
  // transparent fixed root so the equipped backdrop letterboxes around
  // the stage, and the room itself is exactly stage.w × stage.h. The
  // first cut centred a 960px panel with `alignItems:center` inside a
  // fixed root — a flex-centred child taller than its box is clipped at
  // the TOP and cannot be scrolled to, so the header was off-screen.
  const stage = useStageRect();
  useModalKeys(back);   // ESC leaves the room, same as every other room
  return (
    <div style={{
      position: "fixed", inset: 0, background: "transparent",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", fontFamily: GM.font, color: GM.vellum,
    }}>
      <div style={{
        position: "relative", width: stage.w, height: stage.h, flexShrink: 0,
        background: OBSIDIAN_BG, display: "flex", flexDirection: "column", overflow: "hidden",
        boxSizing: "border-box", padding: "14px 14px 12px",
      }}>
        {/* THE TREATMENT: Back on the LEFT, the header inside a 600px
            column, and the right rail left to the overlay's MENU pill —
            the first cut put BACK top-right, exactly under the pill,
            and it could not be tapped. Same row the Codex uses. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10, flexShrink: 0, maxWidth: 600, width: "100%", margin: "0 auto 10px", paddingRight: 8 }}>
          <button onClick={back} style={S.tab(false)}>← BACK</button>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontFamily: GM.fontD, fontSize: 18, color: GM.inkGoldBright, letterSpacing: 3, textShadow: `0 0 12px ${GM.inkGold}66` }}>THE EXCHANGE POST</div>
            <div style={S.small}>cards change hands · dupes become coin</div>
          </div>
          <ChipBadge icon={COIN} color={GM.inkGoldBright}>{fmt(coins)}</ChipBadge>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexShrink: 0, maxWidth: 600, width: "100%", margin: "0 auto 10px" }}>
          <button onClick={() => setTab("trade")} style={S.tab(tab === "trade")}>◆ TRADE</button>
          <button onClick={() => setTab("sell")} style={S.tab(tab === "sell")}>◕ SELL</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 4 }}>
          <OrnatePanel style={{ width: "100%", maxWidth: 600, margin: "0 auto", boxSizing: "border-box", padding: "22px 16px" }}>
            {tab === "trade"
              ? <TradeTab uid={uid} playerName={playerName} profile={profile} initialTradeId={initialTradeId} friends={friends} onInviteFriend={onInviteFriend} />
              : <SellTab uid={uid} profile={profile} />}
          </OrnatePanel>
        </div>
      </div>
    </div>
  );
}
